import type { AppointmentStatus, SchedulerBooking } from '@/types/scheduler';

/**
 * NOT-320 — the calendar's multi-select filter set. Each array is a disjunction
 * (any selected value matches); the filters combine with AND. An empty array
 * means that filter is inactive (matches everything). Client-side only — applied
 * to the already-fetched range, so toggling a filter is instant (no refetch).
 */
export interface CalendarFilters {
  /** Technician ids + crew ids (a booking matches on either its tech or its crew). */
  assignees: string[];
  statuses: AppointmentStatus[];
  /** serviceType ids — the booking's primary service. */
  eventTypes: string[];
  /** jobType ids — the parent job's type. */
  jobTypes: string[];
  /** serviceType ids — intersected with the job's required skills. */
  skills: string[];
}

export const EMPTY_CALENDAR_FILTERS: CalendarFilters = {
  assignees: [],
  statuses: [],
  eventTypes: [],
  jobTypes: [],
  skills: [],
};

/** Total number of selected values across every filter (drives the toolbar badge). */
export function activeFilterCount(f: CalendarFilters): number {
  return (
    f.assignees.length +
    f.statuses.length +
    f.eventTypes.length +
    f.jobTypes.length +
    f.skills.length
  );
}

export function hasActiveFilters(f: CalendarFilters): boolean {
  return activeFilterCount(f) > 0;
}

/**
 * True when the booking satisfies every active filter (AND across filters, OR
 * within each). An empty filter is skipped.
 */
export function matchesFilters(b: SchedulerBooking, f: CalendarFilters): boolean {
  if (f.assignees.length) {
    // Match any assigned technician (lead or crew helper, via technicianIds) or
    // the assigned crew — mirrors the server-side technicianId filter, which also
    // matches crew assignments, so filtering by a helper doesn't drop the visit.
    const techIds =
      b.technicianIds && b.technicianIds.length
        ? b.technicianIds
        : b.technicianId
          ? [b.technicianId]
          : [];
    const matchesAssignee =
      techIds.some((id) => f.assignees.includes(id)) ||
      (b.crew != null && f.assignees.includes(b.crew.id));
    if (!matchesAssignee) return false;
  }
  if (f.statuses.length && !f.statuses.includes(b.status)) return false;
  if (f.eventTypes.length && !f.eventTypes.includes(b.serviceType.id)) return false;
  if (f.jobTypes.length && !(b.jobType && f.jobTypes.includes(b.jobType.id))) return false;
  if (f.skills.length && !(b.skills ?? []).some((s) => f.skills.includes(s.id))) return false;
  return true;
}

/** Toggle a value in one of the filter arrays, returning a new filters object. */
export function toggleFilterValue<K extends keyof CalendarFilters>(
  filters: CalendarFilters,
  key: K,
  value: CalendarFilters[K][number],
): CalendarFilters {
  const current = filters[key] as string[];
  const next = current.includes(value as string)
    ? current.filter((v) => v !== value)
    : [...current, value as string];
  return { ...filters, [key]: next };
}
